import { useEffect, useMemo, useState } from 'react';
import type { GameId, TournamentMetric } from '@tg/shared';
import { INVITE_COOKIE, isMultiplierMetric } from '@tg/shared';
import { INTL_TAG } from '@/i18n/locales';
import { useFormat } from '@/i18n/useFormat';
import { useLocale } from '@/i18n/useLocale';

/** Friend avatar colours (design Social: pal[i % 8]); offline friends are dim. */
export const AVATAR_PALETTE = ['--teal', '--orange', '--blue', '--gold', '--pink', '--green', '--purple', '--red'] as const;
export const avatarColor = (publicId: number): string => `var(${AVATAR_PALETTE[publicId % AVATAR_PALETTE.length]})`;

/** Podium colours: 1 gold, 2 silver (text-2), 3 orange (design). */
export const PODIUM = ['var(--gold-text)', 'var(--text-2)', 'var(--orange-text)'] as const;

/** Re-render every `ms` (countdowns, "2 min ago"). */
export function useNow(ms = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['day', 86_400_000],
  ['hour', 3_600_000],
  ['minute', 60_000],
];

/**
 * "2 hours ago" / "2h ago" (Intl.RelativeTimeFormat). CLDR's Russian `narrow` past is "-4 мин" (reads as a
 * negative number), so Russian falls back to `short` ("4 мин. назад").
 */
export function relTimeFormatter(tag: string, style: Intl.RelativeTimeFormatStyle = 'long') {
  const eff = style === 'narrow' && tag.startsWith('ru') ? 'short' : style;
  const rtf = new Intl.RelativeTimeFormat(tag, { numeric: 'auto', style: eff });
  return (iso: string | number | Date, now = Date.now()) => {
    const diff = new Date(iso).getTime() - now;
    for (const [unit, size] of UNITS) {
      if (Math.abs(diff) >= size || unit === 'minute') return rtf.format(Math.round(diff / size), unit);
    }
    return rtf.format(0, 'minute');
  };
}

/** Relative time in the URL locale. */
export function useRelTime(style: Intl.RelativeTimeFormatStyle = 'long') {
  const locale = useLocale();
  return useMemo(() => relTimeFormatter(INTL_TAG[locale], style), [locale, style]);
}

/** Score cell: multiples ×100 → "48.20×", tokens → "2,400". */
export function useScoreText() {
  const f = useFormat();
  return (metric: TournamentMetric, score: number) => (isMultiplierMetric(metric) ? f.mult(score / 100) : f.tokens(score));
}

/** Games played at a shared table (common rounds, a friend can sit at the same one). */
export const SHARED_TABLES: readonly GameId[] = ['crash', 'pot', 'roulette', 'hongbao', 'mahjong-table', 'doudizhu'];
export const isSharedTable = (g: GameId): boolean => SHARED_TABLES.includes(g);

/** Forget the invite code once signed in (the server only reads it on sign-up). */
export function clearInviteCookie(): void {
  if (document.cookie.includes(`${INVITE_COOKIE}=`)) document.cookie = `${INVITE_COOKIE}=; path=/; max-age=0; samesite=lax`;
}
